import { useEffect, useState } from "react";
import { RotateCcw, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";

const CONFIRM_WORD = "RESET";

export const ChannelResetDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(null);
  const [channels, setChannels] = useState(true);
  const [priv, setPriv] = useState(false);
  const [channelId, setChannelId] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setConfirm(""); setChannels(true); setPriv(false); setChannelId(""); setOpts(null);
    api.get("/admin/channels/reset-options").then(({ data }) => setOpts(data)).catch(() => toast.error("Channels konnten nicht geladen werden"));
  }, [open]);

  const selected = opts?.channels.find((c) => c.id === channelId);
  const chCount = channelId ? selected?.messages ?? 0 : opts?.channels.reduce((s, c) => s + c.messages, 0) ?? 0;
  const canSubmit = (channels || priv) && confirm.trim().toUpperCase() === CONFIRM_WORD && !busy;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/channels/reset", { channels, private: priv, channel_id: channels && channelId ? channelId : null });
      toast.success(`${data.channel_messages_deleted} Channel- + ${data.private_messages_deleted} Privat-Nachrichten gelöscht`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Zurücksetzen");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="open-channel-reset-btn" className="shrink-0 inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold px-3 py-2 rounded-full transition-colors">
        <RotateCcw className="w-3.5 h-3.5" /> Channels zurücksetzen
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="channel-reset-dialog" className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900"><AlertTriangle className="w-5 h-5 text-rose-600" /> Chats zurücksetzen</DialogTitle>
            <DialogDescription>Gelöschte Nachrichten können nicht wiederhergestellt werden. Channels und Mitglieder bleiben erhalten.</DialogDescription>
          </DialogHeader>
          {!opts ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
          ) : (
            <div className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={channels} onCheckedChange={(v) => setChannels(!!v)} data-testid="reset-channels-checkbox" className="mt-0.5" />
                <span className="text-sm text-slate-800">Channel-Nachrichten <span className="text-slate-500">({chCount})</span></span>
              </label>
              {channels && (
                <select value={channelId} onChange={(e) => setChannelId(e.target.value)} data-testid="reset-channel-select" className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-[#1D5BFF]">
                  <option value="">Alle Channels</option>
                  {opts.channels.map((c) => (
                    <option key={c.id} value={c.id}>{c.group} · #{c.name} ({c.messages})</option>
                  ))}
                </select>
              )}
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={priv} onCheckedChange={(v) => setPriv(!!v)} data-testid="reset-private-checkbox" className="mt-0.5" />
                <span className="text-sm text-slate-800">Alle privaten Nachrichten (DMs) <span className="text-slate-500">({opts.private_messages})</span></span>
              </label>
              <div>
                <p className="text-xs text-slate-600 mb-1.5">Zur Bestätigung <b>{CONFIRM_WORD}</b> eingeben:</p>
                <input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" placeholder={CONFIRM_WORD} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-rose-500" />
              </div>
              <button onClick={submit} disabled={!canSubmit} data-testid="reset-submit-btn" className="w-full inline-flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold py-2.5 rounded-full transition-colors">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Jetzt unwiderruflich löschen
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
